// Partners.jsx avec vue en tableau et tabs par statut

import React, { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import AddPartnerForm from '../../components/partners/AddPartnerForm';
import { ApiContext } from '../../App';
import Loading from '../../components/utils/Loading';
import Breadcrumb from '../../components/utils/Breadcrumb';
import ConfirmationDialog from '../../components/utils/ConfirmationDialog';

const Partners = () => {
  const API = useContext(ApiContext);
  const [partners, setPartners] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortField, setSortField] = useState('name');
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // all, cold, prospect, client, old client
  const [loading, setLoading] = useState(true);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState(null);

  // Charger les partenaires existants
  const fetchPartners = async () => {
    setLoading(true);
    try {
      const response = await API.get('/api/partners');
      setPartners(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des partenaires:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  // Fonction pour gérer la recherche par nom
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Fonction pour trier les partenaires
  const handleSort = (field) => {
    const newSortOrder = field === sortField && sortOrder === 'asc' ? 'desc' : 'asc';
    
    const sortedPartners = [...partners].sort((a, b) => {
      if (field === 'name') {
        return newSortOrder === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      } else if (field === 'openPositions') {
        return newSortOrder === 'asc'
          ? a.openPositions.length - b.openPositions.length
          : b.openPositions.length - a.openPositions.length;
      } else if (field === 'status') {
        const statusOrder = { 'cold': 0, 'prospect': 1, 'client': 2, 'old client': 3 };
        return newSortOrder === 'asc'
          ? statusOrder[a.status || 'cold'] - statusOrder[b.status || 'cold']
          : statusOrder[b.status || 'cold'] - statusOrder[a.status || 'cold'];
      } else if (field === 'employeeCount') {
        return newSortOrder === 'asc'
          ? (a.employeeCount || 0) - (b.employeeCount || 0)
          : (b.employeeCount || 0) - (a.employeeCount || 0);
      }
      return 0;
    });
    
    setPartners(sortedPartners);
    setSortOrder(newSortOrder);
    setSortField(field);
  };

  // Filtrer les partenaires par nom et statut
  const getFilteredPartners = () => {
    let filtered = partners.filter((partner) =>
      partner.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (activeTab !== 'all') {
      filtered = filtered.filter((partner) => 
        (partner.status || 'cold') === activeTab
      );
    }

    return filtered;
  };

  const filteredPartners = getFilteredPartners();

  // Gestionnaires de sélection
  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    if (newSelectAll) {
      setSelectedPartners(filteredPartners.map(partner => partner._id));
    } else {
      setSelectedPartners([]);
    }
  };

  const handlePartnerSelect = (partnerId) => {
    setSelectedPartners(prev => {
      const newSelection = prev.includes(partnerId)
        ? prev.filter(id => id !== partnerId)
        : [...prev, partnerId];
      
      // Mettre à jour selectAll en fonction de la nouvelle sélection
      setSelectAll(newSelection.length === filteredPartners.length);
      
      return newSelection;
    });
  };

  // Actions en lot
  const handleBulkDelete = async () => {
    try {
      // Supprimer chaque partenaire sélectionné
      for (const partnerId of selectedPartners) {
        await API.delete(`/api/partners/${partnerId}`);
      }

      // Mettre à jour la liste locale
      setPartners(prev => prev.filter(partner => !selectedPartners.includes(partner._id)));
      setSelectedPartners([]);
      setSelectAll(false);
      setShowBulkActions(false);
      setShowBulkDeleteDialog(false);
    } catch (error) {
      console.error('Erreur lors de la suppression en lot:', error);
      alert('Erreur lors de la suppression des entreprises');
    }
  };

  const handleDeletePartner = async () => {
    if (!partnerToDelete) return;

    try {
      const response = await API.delete(`/api/partners/${partnerToDelete}`);
      console.log('Server response:', response);
      setPartners((prev) => prev.filter((partner) => partner._id !== partnerToDelete));
      setShowDeleteDialog(false);
      setPartnerToDelete(null);
    } catch (error) {
      console.error(`Erreur lors de la suppression de l'entreprise:`, error.response || error);
    }
  };

  // Fonction pour obtenir une couleur de badge basée sur le nombre de postes ouverts
  const getPositionBadgeColor = (count) => {
    if (count === 0) return "bg-gray-100 text-gray-700";
    if (count < 3) return "bg-blue-100 text-blue-700";
    if (count < 6) return "bg-indigo-100 text-indigo-700";
    return "bg-purple-100 text-purple-700";
  };

  // Fonction pour obtenir la couleur du badge de statut
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'cold': return 'bg-gray-100 text-gray-700';
      case 'prospect': return 'bg-yellow-100 text-yellow-700';
      case 'client': return 'bg-green-100 text-green-700';
      case 'old client': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Calculer les statistiques pour les tabs
  const getTabCounts = () => {
    const counts = {
      all: partners.length,
      cold: partners.filter(p => (p.status || 'cold') === 'cold').length,
      prospect: partners.filter(p => p.status === 'prospect').length,
      client: partners.filter(p => p.status === 'client').length,
      'old client': partners.filter(p => p.status === 'old client').length
    };
    return counts;
  };

  const tabCounts = getTabCounts();

  const tabs = [
    { id: 'all', label: 'Tous', count: tabCounts.all },
    { id: 'cold', label: 'Cold', count: tabCounts.cold },
    { id: 'prospect', label: 'Prospects', count: tabCounts.prospect },
    { id: 'client', label: 'Clients', count: tabCounts.client },
    { id: 'old client', label: 'Anciens Clients', count: tabCounts['old client'] }
  ];

  // Afficher le loading pendant le chargement
  if (loading) {
    return (
      <div className="bg-gray-100">
        <Loading message="Chargement des entreprises..." fullHeight={true} />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6  lg:p-10 bg-gray-100 min-h-screen pt-16">
      {/* Breadcrumb */}
      <div className='pt-10'>
        <Breadcrumb items={[
        { label: 'Tableau de bord', href: '/app', icon: 'bi-house' },
        { label: 'Entreprises', href: null, icon: 'bi-building', isActive: true }
      ]} />
      </div>
      

      <div className='flex flex-col sm:flex-row items-start sm:items-center mb-4 sm:mb-6 justify-between gap-4'>
        <h1 className="text-2xl sm:text-3xl font-bold m-0">Entreprises</h1>
        
        {/* ✅ Bouton Nouveau Deal */}
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 
                     text-white rounded-lg font-medium transition-colors shadow-sm self-start sm:self-auto"
          >
            <i className="bi bi-plus-lg"></i>
            <span className="hidden sm:inline">Ajouter Entreprise</span>
            <span className="sm:hidden">Ajouter</span>
          </button>
      </div>

      

      {/* Tabs */}
      <div className="mb-4 sm:mb-6">
        <div className="border-b border-slate-300">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                <span className={`ml-1 sm:ml-2 px-1.5 sm:px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id 
                    ? 'bg-indigo-100 text-indigo-600' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Barre de recherche et options de tri */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between     mb-4 sm:mb-6 gap-4">
        <div className='w-full lg:w-auto flex items-center gap-4'>
          <div className='relative items-center'>
            <input
              type="text"
              placeholder="Rechercher par nom d'entreprise"
              value={searchTerm}
              onChange={handleSearchChange}
              className="px-6 py-2 pl-10 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-full sm:w-64"
            />
            <i className="bi bi-search absolute left-3 top-2 text-gray-400"></i>
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
              >
                <i className="bi bi-x"></i>
              </button>
            )}
          </div>
          
          {/* Bouton d'actions en lot */}
          {selectedPartners.length > 0 && (
            <button
              onClick={() => setShowBulkActions(true)}
              className="flex items-center px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm"
            >
              <i className="bi bi-check-square mr-2"></i>
              Actions ({selectedPartners.length})
            </button>
          )}
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs sm:text-sm text-gray-600 hidden sm:inline">Trier par :</span>
          <button 
            onClick={() => handleSort('name')} 
            className={`px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm transition ${
              sortField === 'name' 
                ? 'bg-indigo-100 text-indigo-700 font-medium' 
                : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            Nom {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
          </button>
          <button 
            onClick={() => handleSort('status')} 
            className={`px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm transition ${
              sortField === 'status' 
                ? 'bg-indigo-100 text-indigo-700 font-medium' 
                : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            Statut {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
          </button>
          <button 
            onClick={() => handleSort('employeeCount')} 
            className={`px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm transition hidden sm:inline-block ${
              sortField === 'employeeCount' 
                ? 'bg-indigo-100 text-indigo-700 font-medium' 
                : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            Employés {sortField === 'employeeCount' && (sortOrder === 'asc' ? '↑' : '↓')}
          </button>
          <button 
            onClick={() => handleSort('openPositions')} 
            className={`px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm transition ${
              sortField === 'openPositions' 
                ? 'bg-indigo-100 text-indigo-700 font-medium' 
                : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            Postes {sortField === 'openPositions' && (sortOrder === 'asc' ? '↑' : '↓')}
          </button>
        </div>
      </div>

      {/* Modal AddPartnerForm */}
      {showForm && (
        <AddPartnerForm
          onClose={() => setShowForm(false)}
          onSuccess={fetchPartners}
        />
      )}

      {/* Message si aucun résultat */}
      {filteredPartners.length === 0 && (
        <div className="bg-white border rounded-lg p-6 sm:p-8 text-center ">
          <i className="bi bi-search text-3xl sm:text-4xl text-gray-400 mb-2"></i>
          <p className="text-lg font-semibold text-gray-700 sm:text-lg mb-2">
            Aucune entreprise trouvée
          </p>
           <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 
                     text-white rounded-lg font-medium transition-colors shadow-sm self-start sm:self-auto "
          >
            <i className="bi bi-plus-lg"></i>
            <span className="hidden sm:inline">Ajouter Entreprise</span>
            <span className="sm:hidden">Ajouter</span>
          </button>
        </div>
      )}

      {/* Tableau des partenaires */}
      {filteredPartners.length > 0 && (
        <div className="bg-white rounded-lg  border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-indigo-600 bg-white border-gray-300 rounded focus:ring-indigo-500 focus:ring-2"
                    />
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entreprise
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Localisation
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Employés
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Postes
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Technologies
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPartners.map((partner) => (
                  <tr key={partner._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-4 text-center">
                      <input
                        type="checkbox"
                        checked={selectedPartners.includes(partner._id)}
                        onChange={() => handlePartnerSelect(partner._id)}
                        className="w-4 h-4 text-indigo-600 bg-white border-gray-300 rounded focus:ring-indigo-500 focus:ring-2"
                      />
                    </td>
                    <td className="px-3 sm:px-6 py-4">
                      <div className="flex flex-col">
                        <Link 
                          to={`/partners/${partner._id}`} 
                          className="flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-900 hover:underline"
                        >
                          {partner.photo && (
                            <img src={partner.photo} alt="Logo Entreprise" className='w-6 h-6' />
                          )} 
                          {partner.name}
                        </Link>
                        <div className="text-xs sm:text-sm text-gray-500 sm:hidden">{partner.city}</div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(partner.status || 'cold')}`}>
                        {partner.status || 'cold'}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-sm text-gray-900 hidden sm:table-cell">
                      <div className="flex items-center">
                        <i className="bi bi-geo-alt text-gray-400 mr-1"></i>
                        {partner.city || 'Non spécifié'}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 text-sm text-gray-900 hidden lg:table-cell">
                      <div className="flex items-center">
                        <i className="bi bi-people text-gray-400 mr-1"></i>
                        {partner.employeeCount || 'N/A'}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPositionBadgeColor(partner.openPositions.length)}`}>
                        {partner.openPositions.length}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-4 hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {partner.technologies && partner.technologies.length > 0 ? (
                          partner.technologies.slice(0, 2).map((tech, index) => (
                            <span key={index} className="inline-flex px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                              {tech}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-gray-400">Aucune</span>
                        )}
                        {partner.technologies && partner.technologies.length > 2 && (
                          <span className="text-xs text-gray-500">+{partner.technologies.length - 2}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Panneau d'actions en lot */}
      {showBulkActions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-[90vw]">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                Actions pour {selectedPartners.length} entreprise(s)
              </h3>
              <button
                onClick={() => setShowBulkActions(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => setShowBulkDeleteDialog(true)}
                className="w-full flex items-center px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <i className="bi bi-trash mr-3"></i>
                <div className="text-left">
                  <div className="font-medium">Supprimer les entreprises</div>
                  <div className="text-sm text-gray-500">
                    Supprimer définitivement les {selectedPartners.length} entreprise(s) sélectionnée(s)
                  </div>
                </div>
              </button>
            </div>

            <div className="flex justify-end mt-6 pt-4 border-t">
              <button
                onClick={() => setShowBulkActions(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog for Bulk Delete */}
      <ConfirmationDialog
        isOpen={showBulkDeleteDialog}
        onClose={() => setShowBulkDeleteDialog(false)}
        onConfirm={handleBulkDelete}
        title="Supprimer les entreprises"
        message={`Êtes-vous sûr de vouloir supprimer ${selectedPartners.length} entreprise(s) ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        isDangerous={true}
      />

      {/* Confirmation Dialog for Single Delete */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setPartnerToDelete(null);
        }}
        onConfirm={handleDeletePartner}
        title="Supprimer l'entreprise"
        message="Êtes-vous sûr de vouloir supprimer cette entreprise ? Cette action est irréversible."
        confirmText="Supprimer"
        cancelText="Annuler"
        isDangerous={true}
      />
    </div>
  );
};

export default Partners;